/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

import { css, jsx } from '@compiled/react';

import { ExperienceNames } from '../experience-tracker';
import { type ALLOWED_FEATURES_TYPE, type AllowedFeatures } from '../features';
import {
	EMBEDDED_CONFLUENCE_CONTENT_TYPE,
	EMBEDDED_CONFLUENCE_MODE,
	type PageCommonProps,
	useContentSize,
} from '../page-common';
import { Content } from '../content';
import { useGetCurrentScrollPosition } from '../page-common/useGetCurrentScrollPosition';
import { useUniqueKey } from '../unique-key-provider/UniqueKeyProvider';

const embeddedPageContainerSytles = css({
	display: 'flex',
	flexDirection: 'column',
});

const StyledEmbeddedPageContainer = ({
	children,
	containerHeight,
}: {
	children: React.ReactNode;
	containerHeight: string;
}) => {
	return (
		<div
			data-testId="styled-embedded-page-container-id"
			style={{ height: containerHeight }}
			css={embeddedPageContainerSytles}
		>
			{children}
		</div>
	);
};

export type ViewPageProps = PageCommonProps & {
	allowedFeatures?: AllowedFeatures<ALLOWED_FEATURES_TYPE.VIEW>;
	contentId: string;
	/**
	 * Currently the view component supports JSM's two views - Portal and Agent View.
	 * There is a concern with both the footer at the bottom and the `powered by` being shown in portal view but not in agent view.
	 * But Embedded Component wants people to see the Confluence branding.
	 * In order to circumvent that we have introduced a property `hasFooterLogo` to be used by JSM portal view.
	 *
	 * EDIT 7/17: hasFooterLogo has been renamed internally for clarity as is now aliased as shouldShowFooter when used.
	 */
	hasFooterLogo?: boolean;
	parentProduct: string;
	showFooter?: boolean;
};

export const ViewPage = (props: ViewPageProps) => {
	const { hasFooterLogo: shouldShowFooter, isHeightSetFromContent, showFooter = true } = props;
	const { uniqueKey } = useUniqueKey();
	const { height } = useContentSize(uniqueKey);
	useGetCurrentScrollPosition(isHeightSetFromContent);

	const contentConfiguration = useMemo(
		() => ({
			experiences: {
				experience: ExperienceNames.VIEW_PAGE,
				ccExperience: ExperienceNames.CC_VIEW_PAGE,
			},
			getWrapperElement: (children: React.ReactNode): React.ReactElement => (
				<StyledEmbeddedPageContainer
					containerHeight={isHeightSetFromContent ? `${height}px` : '100%'}
				>
					{children}
				</StyledEmbeddedPageContainer>
			),
			hasFooter: showFooter,
		}),
		[isHeightSetFromContent, height, showFooter],
	);

	return (
		<Content
			{...props}
			contentType={EMBEDDED_CONFLUENCE_CONTENT_TYPE.PAGE}
			mode={EMBEDDED_CONFLUENCE_MODE.VIEW_MODE}
			contentConfiguration={contentConfiguration}
			isHeightSetFromContent={isHeightSetFromContent}
			shouldShowFooter={shouldShowFooter}
		/>
	);
};
